import { authenticatedRequest } from '../api/newStudent/apiStudent.service';
import {
  IChatUser,
  IChatConversation,
  IChatMessage,
  IGroupParticipant,
} from '../../interfaces/chat/chat.interface';

const chatService = {
  async getCurrentUser(): Promise<IChatUser | null> {
    try {
      const response = await authenticatedRequest('/chat/user/current', 'GET');
      if (response?.result && response?.data) {
        return response.data;
      }
      return null;
    } catch (error) {
      return null;
    }
  },

  async updateOnlineStatus(isOnline: boolean): Promise<boolean> {
    try {

      const { apiUrl } = await import('../../variables');
      const token = localStorage.getItem('userStudent');
      
      if (!token || token === 'null') {
        return false;
      }
      
      const response = await fetch(`${apiUrl}/chat/user/update-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ isOnline: Boolean(isOnline) }),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data?.result || false;
    } catch (error) {
      return false;
    }
  },

  async getAllUsers(): Promise<IChatUser[]> {
    try {
      const response = await authenticatedRequest('/chat/users', 'GET');
      if (response?.result && response?.data?.users) {
        return response.data.users;
      }
      return [];
    } catch (error) {
      return [];
    }
  },

  async getConversations(): Promise<IChatConversation[]> {
    try {
      const response = await authenticatedRequest('/chat/conversations', 'GET');
      if (response?.result && response?.data?.conversations) {
        return response.data.conversations;
      }
      return [];
    } catch (error) {
      return [];
    }
  },

  async checkExistingConversation(userId1: number, userId2: number): Promise<number | null> {
    try {
      const response = await authenticatedRequest('/chat/conversations/check-existing', 'POST', {
        userId1,
        userId2,
      });
      if (response?.result && response?.data?.conversationId) {
        return response.data.conversationId;
      }
      return null;
    } catch (error) {
      return null;
    }
  },

  async createDirectConversation(userId1: number, userId2: number): Promise<number | null> {
    try {
      const response = await authenticatedRequest('/chat/conversations/direct', 'POST', {
        userId1,
        userId2,
      });
      if (response?.result && response?.data?.conversationId) {
        return response.data.conversationId;
      }
      return null;
    } catch (error) {
      return null;
    }
  },

  async createGroupConversation(
    name: string,
    creatorId: number,
    participantIds: number[],
  ): Promise<number | null> {
    try {
      const response = await authenticatedRequest('/chat/conversations/group', 'POST', {
        name,
        creatorId,
        participantIds,
      });
      if (response?.result && response?.data?.conversationId) {
        return response.data.conversationId;
      }
      return null;
    } catch (error) {
      return null;
    }
  },

  async getMessages(
    conversationId: number,
    page: number = 1,
    limit: number = 50,
  ): Promise<{ messages: IChatMessage[]; hasMore: boolean; total?: number }> {
    try {
      const response = await authenticatedRequest(
        `/chat/conversations/${conversationId}/messages?page=${page}&limit=${limit}`,
        'GET',
      );
      if (response?.result && response?.data) {
        return {
          messages: response.data.messages || [],
          hasMore: response.data.hasMore || false,
          total: response.data.total,
        };
      }
      return { messages: [], hasMore: false };
    } catch (error) {
      return { messages: [], hasMore: false };
    }
  },

  async sendMessage(conversationId: number, content: string): Promise<boolean> {
    try {
      const response = await authenticatedRequest('/chat/messages', 'POST', {
        conversationId,
        content,
      });
      return response?.result || false;
    } catch (error) {
      return false;
    }
  },

  async markAsRead(conversationId: number): Promise<boolean> {
    try {
      const response = await authenticatedRequest('/chat/messages/mark-as-read', 'POST', {
        conversationId,
      });
      return response?.result || false;
    } catch (error) {
      return false;
    }
  },

  async markAsUnread(conversationId: number): Promise<boolean> {
    try {
      const response = await authenticatedRequest('/chat/conversations/mark-as-unread', 'POST', {
        conversationId,
      });
      return response?.result || false;
    } catch (error) {
      return false;
    }
  },

  async deleteConversation(conversationId: number): Promise<boolean> {
    try {
      const response = await authenticatedRequest('/chat/conversations/delete', 'POST', {
        conversationId,
      });
      return response?.result || false;
    } catch (error) {
      return false;
    }
  },

  async toggleFavorite(conversationId: number, isFavorited: boolean): Promise<boolean> {
    try {
      const response = await authenticatedRequest('/chat/conversations/toggle-favorite', 'POST', {
        conversationId,
        isFavorited,
      });
      return response?.result || false;
    } catch (error) {
      return false;
    }
  },

  async sendBulkMessage(recipientUserIds: number[], content: string): Promise<boolean> {
    try {
      const response = await authenticatedRequest('/chat/messages/send-bulk', 'POST', {
        recipientUserIds,
        content,
      });
      return response?.result || false;
    } catch (error) {
      return false;
    }
  },

  async getGroupParticipants(conversationId: number): Promise<IGroupParticipant[]> {
    try {
      const response = await authenticatedRequest(
        `/chat/conversations/${conversationId}/participants`,
        'GET',
      );
      if (response?.result && response?.data?.participants) {
        return response.data.participants;
      }
      return [];
    } catch (error) {
      return [];
    }
  },

  async addGroupParticipants(
    conversationId: number,
    participantIds: number[],
  ): Promise<boolean> {
    try {
      const response = await authenticatedRequest('/chat/conversations/participants/add', 'POST', {
        conversationId,
        participantIds,
      });
      return response?.result || false;
    } catch (error) {
      return false;
    }
  },

  async removeGroupParticipant(conversationId: number, participantId: number): Promise<boolean> {
    try {
      const response = await authenticatedRequest('/chat/conversations/participants/remove', 'POST', {
        conversationId,
        participantId,
      });
      return response?.result || false;
    } catch (error) {
      return false;
    }
  },
};

export default chatService;

